import { useEffect, useState } from 'react';

const LINE_1 = 'DU GOÛT,';
const LINE_2 = 'DU PLAISIR,';
const LINE_3 = 'à chaque bouchée !';

const TYPE_SPEED_MS = 78;
const LINE_PAUSE_MS = 400;
const HANDWRITE_SPEED_MS = 110;
const END_PAUSE_MS = 2200;
const INITIAL_DELAY_MS = 400;

const line1End = INITIAL_DELAY_MS + LINE_1.length * TYPE_SPEED_MS;
const line2Start = line1End + LINE_PAUSE_MS;
const line2End = line2Start + LINE_2.length * TYPE_SPEED_MS;
const line3Start = line2End + LINE_PAUSE_MS;
const cycleDuration = line3Start + LINE_3.length * HANDWRITE_SPEED_MS + END_PAUSE_MS;

function useTypewriter(text, startDelay, speed, enabled, cycleKey) {
  const [count, setCount] = useState(enabled ? 0 : text.length);
  const [active, setActive] = useState(false);
  const [done, setDone] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setCount(text.length);
      setDone(true);
      setActive(false);
      return undefined;
    }

    setCount(0);
    setDone(false);
    setActive(false);

    let interval;

    const startTimer = setTimeout(() => {
      setActive(true);
      let index = 0;

      interval = setInterval(() => {
        index += 1;
        setCount(index);

        if (index >= text.length) {
          clearInterval(interval);
          setActive(false);
          setDone(true);
        }
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (interval) clearInterval(interval);
    };
  }, [text, startDelay, speed, enabled, cycleKey]);

  return {
    text: text.slice(0, count),
    showCursor: active && !done,
    done,
  };
}

function useHandwriting(text, startDelay, speed, enabled, cycleKey) {
  const [count, setCount] = useState(enabled ? 0 : text.length);

  useEffect(() => {
    if (!enabled) {
      setCount(text.length);
      return undefined;
    }

    setCount(0);

    let interval;

    const startTimer = setTimeout(() => {
      let index = 0;

      interval = setInterval(() => {
        index += 1;
        setCount(index);

        if (index >= text.length) {
          clearInterval(interval);
        }
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (interval) clearInterval(interval);
    };
  }, [text, startDelay, speed, enabled, cycleKey]);

  return { count };
}

function HeroAnimatedTitle() {
  const [motionEnabled, setMotionEnabled] = useState(true);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setMotionEnabled(!media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const loopTimer = setTimeout(() => {
      setCycle((current) => current + 1);
    }, cycleDuration);

    return () => clearTimeout(loopTimer);
  }, [cycle, motionEnabled]);

  const line1 = useTypewriter(LINE_1, INITIAL_DELAY_MS, TYPE_SPEED_MS, motionEnabled, cycle);
  const line2 = useTypewriter(LINE_2, line2Start, TYPE_SPEED_MS, motionEnabled, cycle);
  const handwriting = useHandwriting(LINE_3, line3Start, HANDWRITE_SPEED_MS, motionEnabled, cycle);

  return (
    <h1 className="hero-title">
      <span className="hero-line-orange hero-typewriter" aria-label={LINE_1}>
        {line1.text.split('').map((char, index) => (
          <span key={`l1-${cycle}-${index}`} className="hero-typewriter-char">
            {char}
          </span>
        ))}
        {line1.showCursor && <span className="hero-typewriter-cursor" aria-hidden="true">|</span>}
      </span>

      <span className="hero-line-orange hero-typewriter" aria-label={LINE_2}>
        {line2.text.split('').map((char, index) => (
          <span key={`l2-${cycle}-${index}`} className="hero-typewriter-char">
            {char}
          </span>
        ))}
        {line2.showCursor && <span className="hero-typewriter-cursor" aria-hidden="true">|</span>}
      </span>

      <span className="hero-line-script hero-handwriting" aria-label={LINE_3}>
        {LINE_3.split('').map((char, index) => {
          const isVisible = index < handwriting.count;
          const isSpace = char === ' ';

          return (
            <span
              key={`l3-${cycle}-${index}`}
              className={`hero-handwriting-char${isVisible ? ' is-written' : ''}${isSpace ? ' is-space' : ''}`}
            >
              {isSpace ? '\u00A0' : char}
            </span>
          );
        })}
      </span>
    </h1>
  );
}

export default HeroAnimatedTitle;
